import type { ButtonHTMLAttributes, ReactNode } from "react";
import joinClassNames from "../../utils/joinClassNames";

import "./formActionButton.scss";

type FormActionButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "outline-white";
type FormActionButtonSize = "md" | "sm" | "xs";
type FormActionButtonAlign = "center" | "start";

type FormActionButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  children?: ReactNode;
  iconClassName: string;
  variant?: FormActionButtonVariant;
  size?: FormActionButtonSize;
  align?: FormActionButtonAlign;
  fullWidth?: boolean;
  loading?: boolean;
  loadingLabel?: ReactNode;
  iconOnly?: boolean;
};

export default function FormActionButton({
  children,
  iconClassName,
  variant = "primary",
  size = "md",
  align = "center",
  fullWidth = false,
  loading = false,
  loadingLabel,
  iconOnly = false,
  className,
  type = "button",
  ...props
}: FormActionButtonProps) {
  return (
    <button
      type={type}
      className={joinClassNames(
        "form-action-button",
        `form-action-button--${variant}`,
        size === "sm" && "form-action-button--sm",
        size === "xs" && "form-action-button--xs",
        align === "start" && "form-action-button--align-start",
        fullWidth && "form-action-button--full-width",
        iconOnly && "form-action-button--icon-only",
        className
      )}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          {!iconOnly && <span>{loadingLabel ?? children}</span>}
        </>
      ) : (
        <>
          <i className={joinClassNames(iconClassName, "form-action-button__icon")} aria-hidden="true" />
          {!iconOnly && <span>{children}</span>}
        </>
      )}
    </button>
  );
}